import type { Pin, PinCategory } from '@cht/shared'
import type { Table } from 'dexie'
import type { FieldClient } from '../api/field-client.js'
import { newId } from '@cht/shared'

export type PinSyncState = 'queued' | 'failed' | 'synced'
export interface StoredPin extends Pin { syncState: PinSyncState; syncError?: string }
export interface NewPin {
  category: PinCategory
  title: string
  description: string
  latitude: number
  longitude: number
}

/** Persistent, property-scoped pin cache and offline outbox. */
export class PinStore {
  private synchronizing: Promise<void> | undefined

  constructor(
    private readonly propertyId: string,
    private readonly userId: string,
    private readonly pins: Table<StoredPin, string>,
    private readonly client: Pick<FieldClient, 'pins' | 'createPin' | 'updatePin' | 'deletePin'>
  ) {}

  list(): Promise<StoredPin[]> {
    return this.pins.where('propertyId').equals(this.propertyId).reverse().sortBy('updatedAt')
  }

  async load(online: boolean): Promise<StoredPin[]> {
    if (!online) return this.list()
    await this.synchronize()
    try {
      const remote = await this.client.pins()
      const pending = await this.pins.where('[propertyId+syncState]').anyOf(
        [this.propertyId, 'queued'], [this.propertyId, 'failed']
      ).toArray()
      await this.pins.where('[propertyId+syncState]').equals([this.propertyId, 'synced']).delete()
      await this.pins.bulkPut(remote.map((pin) => ({ ...pin, syncState: 'synced' as const })))
      if (pending.length) await this.pins.bulkPut(pending)
    } catch { /* Cached pins and the outbox remain usable. */ }
    return this.list()
  }

  async create(input: NewPin, online: boolean): Promise<StoredPin> {
    const now = Date.now()
    const pin: StoredPin = {
      ...input, id: newId(), propertyId: this.propertyId, authorId: this.userId,
      createdAt: now, updatedAt: now, syncState: 'queued'
    }
    await this.pins.put(pin)
    if (online) await this.send(pin)
    return (await this.pins.get(pin.id)) ?? pin
  }

  async update(pin: StoredPin, input: NewPin, online: boolean): Promise<StoredPin> {
    if (pin.syncState === 'synced') {
      if (!online) throw new Error('Reconnect before editing a pin that is already on the map.')
      const updated = await this.client.updatePin({ id: pin.id, ...input })
      const stored = { ...updated, syncState: 'synced' as const }
      await this.pins.put(stored)
      return stored
    }
    const { syncError: _syncError, ...withoutError } = pin
    const updated: StoredPin = { ...withoutError, ...input, updatedAt: Date.now(), syncState: 'queued' }
    await this.pins.put(updated)
    if (online) await this.send(updated)
    return (await this.pins.get(pin.id)) ?? updated
  }

  async delete(pin: StoredPin, online: boolean): Promise<void> {
    if (pin.syncState === 'synced') {
      if (!online) throw new Error('Reconnect before deleting a pin that is already on the map.')
      await this.client.deletePin(pin.id)
    }
    await this.pins.delete(pin.id)
  }

  async synchronize(): Promise<void> {
    if (this.synchronizing) return this.synchronizing
    this.synchronizing = this.drain()
    try { await this.synchronizing } finally { this.synchronizing = undefined }
  }

  private async drain(): Promise<void> {
    const pending = await this.pins.where('[propertyId+syncState]').anyOf(
      [this.propertyId, 'queued'], [this.propertyId, 'failed']
    ).toArray()
    for (const pin of pending) await this.send(pin)
  }

  private async send(pin: StoredPin): Promise<void> {
    try {
      await this.client.createPin({
        id: pin.id, category: pin.category, title: pin.title, description: pin.description,
        latitude: pin.latitude, longitude: pin.longitude
      })
      const { syncError: _syncError, ...withoutError } = pin
      await this.pins.put({ ...withoutError, syncState: 'synced' })
    } catch (error) {
      await this.pins.put({ ...pin, syncState: 'failed', syncError: error instanceof Error ? error.message : String(error) })
    }
  }
}
